import { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Download, Star, Check, Trash2, Pencil, Save } from 'lucide-react';
import type { Photo } from '@w6/shared';
import { fetchDownloadUrl, useUpdateAlbum, useUpdatePhoto, useDeletePhoto } from '../lib/queries';
import { useAuth } from '../lib/auth';

interface PhotoLightboxProps {
  albumId: string;
  photos: Photo[];
  currentIndex: number;
  currentCoverPhotoId: string | null;
  onClose: () => void;
  onNavigate: (newIndex: number) => void;
  onPhotoDeleted?: () => void;
}

export default function PhotoLightbox({
  albumId,
  photos,
  currentIndex,
  currentCoverPhotoId,
  onClose,
  onNavigate,
  onPhotoDeleted,
}: PhotoLightboxProps) {
  const { user } = useAuth();
  const updateAlbum = useUpdateAlbum(albumId);
  const updatePhoto = useUpdatePhoto(albumId);
  const deletePhoto = useDeletePhoto(albumId);
  const [coverSetForId, setCoverSetForId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [editPhotographer, setEditPhotographer] = useState('');

  const photo = photos[currentIndex];

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (editing) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && currentIndex > 0) onNavigate(currentIndex - 1);
      if (e.key === 'ArrowRight' && currentIndex < photos.length - 1) onNavigate(currentIndex + 1);
    }
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [currentIndex, photos.length, onClose, onNavigate, editing]);

  useEffect(() => {
    setEditing(false);
    if (photo) {
      setEditDescription(photo.description ?? '');
      setEditPhotographer(photo.photographer ?? '');
    }
  }, [photo?.id]);

  if (!photo) return null;

  async function handleDownload() {
    if (!photo) return;
    const url = await fetchDownloadUrl(albumId, photo.id);
    window.location.href = url;
  }

  function handleSetCover() {
    if (!photo) return;
    void (async () => {
      try {
        await updateAlbum.mutateAsync({ coverPhotoId: photo.id });
        setCoverSetForId(photo.id);
      } catch {
        // ignored
      }
    })();
  }

  function handleSaveEdit() {
    if (!photo) return;
    void (async () => {
      try {
        await updatePhoto.mutateAsync({
          photoId: photo.id,
          input: {
            description: editDescription.trim() || null,
            photographer: editPhotographer.trim() || null,
          },
        });
        setEditing(false);
      } catch {
        // ignored — felmeddelande kan visas via mutation.error om vi vill
      }
    })();
  }

  function handleDelete() {
    if (!photo) return;
    if (!confirm('Radera detta foto permanent? Det går inte att ångra.')) return;
    void (async () => {
      try {
        await deletePhoto.mutateAsync(photo.id);
        onPhotoDeleted?.();
        onClose();
      } catch {
        // ignored
      }
    })();
  }

  const isAdmin = user?.role === 'admin';
  const isCurrentCover = currentCoverPhotoId === photo.id || coverSetForId === photo.id;

  return (
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
        aria-label="Stäng"
      >
        <X size={28} />
      </button>

      {currentIndex > 0 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNavigate(currentIndex - 1); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2"
          aria-label="Föregående"
        >
          <ChevronLeft size={36} />
        </button>
      )}

      {currentIndex < photos.length - 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNavigate(currentIndex + 1); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2"
          aria-label="Nästa"
        >
          <ChevronRight size={36} />
        </button>
      )}

      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="max-w-5xl w-full mx-4 flex flex-col items-center"
        onClick={(e) => { e.stopPropagation(); }}
      >
        <img
          src={photo.viewUrl ?? photo.thumbnailUrl ?? ''}
          alt={photo.description ?? ''}
          className="max-h-[70vh] w-auto object-contain rounded-lg shadow-2xl"
        />

        <div className="mt-4 bg-white/10 backdrop-blur rounded-lg px-6 py-3 text-white max-w-2xl w-full">
          {editing ? (
            <div className="space-y-2">
              <label className="block">
                <span className="block text-xs text-white/70 mb-1">Beskrivning</span>
                <input
                  type="text"
                  value={editDescription}
                  onChange={(e) => { setEditDescription(e.target.value); }}
                  maxLength={500}
                  className="w-full px-3 py-1.5 rounded bg-white/20 text-white placeholder-white/50 focus:bg-white/30 focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="block text-xs text-white/70 mb-1">Fotograf</span>
                <input
                  type="text"
                  value={editPhotographer}
                  onChange={(e) => { setEditPhotographer(e.target.value); }}
                  maxLength={120}
                  className="w-full px-3 py-1.5 rounded bg-white/20 text-white placeholder-white/50 focus:bg-white/30 focus:outline-none"
                />
              </label>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={updatePhoto.isPending}
                  className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white px-4 py-1.5 rounded text-sm disabled:opacity-60"
                >
                  <Save size={14} /> Spara
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(false); }}
                  className="bg-white/15 hover:bg-white/25 text-white px-4 py-1.5 rounded text-sm"
                >
                  Avbryt
                </button>
              </div>
            </div>
          ) : (
            <>
              {photo.description && <p className="font-medium">{photo.description}</p>}
              <div className="flex flex-wrap items-center justify-between mt-2 gap-2">
                <p className="text-sm text-white/70">
                  {photo.photographer ? `Foto: ${photo.photographer}` : ''}
                  {photo.photographer && photo.width && photo.height ? ' · ' : ''}
                  {photo.width && photo.height ? `${String(photo.width)}×${String(photo.height)}` : ''}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {isAdmin && (
                    <>
                      <button
                        type="button"
                        onClick={() => { setEditing(true); }}
                        className="flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white px-3 py-1.5 rounded text-sm"
                        aria-label="Redigera foto-info"
                      >
                        <Pencil size={14} /> Redigera
                      </button>
                      <button
                        type="button"
                        onClick={handleSetCover}
                        disabled={updateAlbum.isPending || isCurrentCover}
                        className="flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white px-3 py-1.5 rounded text-sm disabled:opacity-60"
                        aria-label={isCurrentCover ? 'Detta är albumets omslag' : 'Sätt som omslag'}
                      >
                        {isCurrentCover ? <Check size={14} /> : <Star size={14} />}
                        {isCurrentCover ? 'Omslag' : 'Sätt omslag'}
                      </button>
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={deletePhoto.isPending}
                        className="flex items-center gap-2 bg-red-600/80 hover:bg-red-600 text-white px-3 py-1.5 rounded text-sm disabled:opacity-60"
                        aria-label="Radera foto"
                      >
                        <Trash2 size={14} /> Radera
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white px-3 py-1.5 rounded text-sm"
                  >
                    <Download size={14} /> Ladda ner
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
